<template>
  <div class="user-form-page">
    <Head title="Create User" />

    <div class="mb-6">
      <nav class="mb-2 flex items-center gap-2 text-sm text-gray-500">
        <Link href="/users" class="font-medium text-brand-600 hover:text-brand-800">Users</Link>
        <span>/</span>
        <span class="text-gray-700">Create</span>
      </nav>
      <p class="text-xs font-semibold uppercase tracking-[0.14em] text-brand-500">Administration</p>
      <h1 class="mt-1 text-3xl font-bold tracking-tight text-brand-900">Create User</h1>
      <p class="mt-1.5 text-sm text-gray-500">Add an admin or agency account with the right access scope.</p>
    </div>

    <div class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <form @submit.prevent="store">
        <div class="grid grid-cols-1 gap-5 p-6 md:grid-cols-2 md:p-8">
          <text-input v-model="form.first_name" :error="form.errors.first_name" class="w-full" label="First name" />
          <text-input v-model="form.last_name" :error="form.errors.last_name" class="w-full" label="Last name" />
          <text-input v-model="form.email" :error="form.errors.email" class="w-full" label="Email" />
          <text-input
            v-model="form.password"
            :error="form.errors.password"
            class="w-full"
            type="password"
            autocomplete="new-password"
            label="Password"
          />
          <select-input v-model="form.role" :error="form.errors.role" class="w-full" label="Role">
            <option v-for="(label, value) in roles" :key="value" :value="value">{{ label }}</option>
          </select-input>
          <select-input v-model="form.owner" :error="form.errors.owner" class="w-full" label="Owner">
            <option :value="1">Yes</option>
            <option :value="0">No</option>
          </select-input>
          <file-input
            v-model="form.photo"
            :errors="form.errors.photo ? [form.errors.photo] : []"
            class="w-full md:col-span-2"
            type="file"
            accept="image/*"
            label="Photo"
          />
          <agency-scope-fields
            v-if="form.role === 'agency'"
            v-model:codes="form.agent_codes"
            v-model:columns="form.visible_columns"
            class="md:col-span-2"
            :channel-options="channels"
            :column-options="customerColumns"
            :defaults="defaultColumns"
            :channel-error="scopeError('agent_codes')"
            :column-error="scopeError('visible_columns')"
          />
        </div>

        <div
          v-if="Object.keys(form.errors).length"
          class="mx-6 mb-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 md:mx-8"
        >
          <p class="font-semibold">Please fix the following:</p>
          <ul class="mt-2 list-disc space-y-1 pl-5">
            <li v-for="(message, key) in uniqueErrors" :key="key">{{ message }}</li>
          </ul>
        </div>

        <div class="flex items-center justify-end gap-3 border-t border-gray-100 bg-gray-50 px-6 py-4 md:px-8">
          <Link
            href="/users"
            class="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </Link>
          <loading-button
            :loading="form.processing"
            class="inline-flex items-center justify-center rounded-xl bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-700"
            type="submit"
          >
            Create User
          </loading-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import FileInput from '@/Shared/FileInput'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import AgencyScopeFields from '@/Shared/AgencyScopeFields'

export default {
  components: {
    AgencyScopeFields,
    FileInput,
    Head,
    Link,
    LoadingButton,
    SelectInput,
    TextInput,
  },
  layout: Layout,
  remember: 'form',
  props: {
    roles: Object,
    channels: Array,
    customerColumns: Array,
    defaultColumns: Array,
  },
  data() {
    return {
      form: this.$inertia.form({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        owner: 0,
        role: 'admin',
        agent_codes: [],
        visible_columns: [...(this.defaultColumns || [])],
        photo: null,
      }),
    }
  },
  computed: {
    uniqueErrors() {
      const seen = new Set()
      const messages = []
      Object.values(this.form.errors || {}).forEach((message) => {
        if (!message || seen.has(message)) return
        seen.add(message)
        messages.push(message)
      })
      return messages.slice(0, 8)
    },
  },
  methods: {
    scopeError(prefix) {
      if (this.form.errors[prefix]) return this.form.errors[prefix]
      const match = Object.keys(this.form.errors || {}).find(
        (key) => key === prefix || key.startsWith(`${prefix}.`),
      )
      return match ? this.form.errors[match] : null
    },
    store() {
      this.form
        .transform((data) => ({
          ...data,
          owner: data.owner === true || data.owner === 1 || data.owner === '1',
          agent_codes: [...(data.agent_codes || [])].map(String),
          visible_columns: [...(data.visible_columns || [])].map(String),
        }))
        .post('/users')
    },
  },
}
</script>
